<template>
  <div :id="component_id" class="message_card bg-surface-1 border-b border-border-separator flex flex-row items-start justify-start"
       :class="clickable ? 'cursor-pointer' : 'cursor-default'" @click="clickable ? $emit('selectThread') : null">
    <div class="message_card_avatar_container">
      <template v-if="threadType==='Messages'">
        <div v-for="(userParticipant, idx) in user_participants.slice(0, max_participants)"
             :key="`${component_key}_user_avatar_${idx}`"
             class="relative rounded-full flex-shrink-0"
             :style="idx > 0 ? 'margin-left: -12px; box-shadow: 0 0 0 2px var(--background-page);' : ''">
          <messenger-avatar
            :name="getParticipantName(userParticipant, idx)"
            :icon="getParticipantName(userParticipant, idx) ? '' : $FUSresolveGhostUser(userParticipant).emoji"
            :size="32"
            :bg-color="getParticipantBgColor(userParticipant, idx)" />
        </div>
      </template>
      <template v-else-if="threadType==='Threads'">
        <messenger-avatar :key="`${component_key}_thread_avatar`" class="participant_avatar" :name="messageThread.name" :size="32" :icon-src="avatar_img" :bg-color="avatar_bg_color" :image-zoom="avatar_img_zoom" :monochrome-icon="avatar_img_is_glyph" />
      </template>
      <template v-else-if="threadType==='Channels'">
        <messenger-avatar :key="`${component_key}_channel_avatar`" class="participant_avatar" :name="messageThread.name" :size="32" :bg-color="avatar_bg_color" />
      </template>
      <template v-else-if="threadType==='Support'">
        <messenger-avatar :key="`${component_key}_thread_avatar`" class="participant_avatar" :name="messageThread.name" :size="32" :icon-src="tac_chat_logo" :image-zoom="avatar_img_zoom" />
      </template>
      <i class="message_card_avatar_icon text-text-inverse" :class="[{'ri-chat-3-fill':threadType==='Messages' && !messageThread.isGroupDm},{'ri-group-fill':threadType==='Messages' && messageThread.isGroupDm},{'ri-hashtag':threadType==='Threads'},{'ri-group-2-fill':threadType==='Channels'},{'ri-customer-service-2-fill':threadType==='Support'}]" />
      <i class="message_card_avatar_icon" :class="[{'ri-chat-3-line':threadType==='Messages' && !messageThread.isGroupDm},{'ri-group-line':threadType==='Messages' && messageThread.isGroupDm},{'ri-hashtag':threadType==='Threads'},{'ri-team-line':threadType==='Channels'},{'ri-customer-service-2-fill':threadType==='Support'}]" />
    </div>
    <div class="message_card_text flex flex-col items-start justify-center h-full">
      <div class="message_card_text_header" :class="{'font-extrabold':unreadMessages>0}">
        <template v-if="threadType==='Threads'">#</template>{{ messageThread.shortName || messageThread.name }}
      </div>
      <div v-if="lastMessage" class="message_card_text_body">{{ lastMessage }}</div>
    </div>
    <div v-if="isCurrent" class="message_card_time_container">
      <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap bg-[var(--background-layer-02)] text-text-subtle">{{ $t('messaging.thread.currentChat') }}</span>
    </div>
    <div v-else-if="formatted_time" class="message_card_time_container">
      <div class="message_card_time">{{ formatted_time }}</div>
    </div>
  </div>
</template>

<script>
import MessengerAvatar from '@components/Messaging/MessengerAvatar.vue';
import SparkIcon from '@images/spark_icon.svg';
import TacChatLogo from '@images/tac_chat_logo.svg';
import CalendarLine from '@images/calendar_line.svg';
import CubeLine from '@images/cube_line.svg';
import RoadsterLine from '@images/roadster_line.svg';
import UserLine from '@images/user_line.svg';
import CheckLine from '@images/check_line.svg';
import MoneyDollarCircleLine from '@images/money_dollar_circle_line.svg';
import ToolsLine from '@images/tools_line.svg';
import { getActiveLocaleTag } from '../../i18n';

export default {
  components: {
    'messenger-avatar': MessengerAvatar,
  },
  props: {
    threadType: {
      type: String,
      required: false,
      default: null,
    },
    currentUserGuid: {
      type: String,
      required: false,
      default: null,
    },
    usersByGuid: {
      type: Object,
      required: false,
      default() {
        return {};
      }
    },
    messageThread: {
      type: Object,
      required: true
    },
    avatarColors: {
      type: Object,
      required: false,
      default: null
    },
    unreadMessages: {
      type: Number,
      required: false,
      default: 0
    },
    lastMessage: {
      type: String,
      required: false,
      default: null,
    },
    lastMessageAt: {
      type: String,
      required: false,
      default: null,
    },
    isCurrent: {
      // When true, this row represents the conversation the user is already in: it renders a
      // "Current chat" notifier. This is display-only — it does not block clicking; use the
      // clickable prop for that.
      type: Boolean,
      required: false,
      default: false,
    },
    clickable: {
      // When false, the row is rendered non-clickable (no cursor change, no selectThread emit).
      // Used by the contact profile's "Shared chats" list to block navigation when the profile
      // was opened from a thread/channel participant list, which would otherwise let the user
      // loop back into chat details indefinitely.
      type: Boolean,
      required: false,
      default: true,
    },
  },
  data() {
    return {
      img_failed: false,
      default_text_color: '#FFFFFF',
      default_bg_color: '#A9B3CA',
      max_participants: 3,
      spark_icon: SparkIcon,
      tac_chat_logo: TacChatLogo,
      calendar_line: CalendarLine,
      cube_line: CubeLine,
      roadster_line: RoadsterLine,
      user_line: UserLine,
      check_line: CheckLine,
      money_dollar_circle_line: MoneyDollarCircleLine,
      tools_line: ToolsLine,
    }
  },
  computed: {
    formatted_time() {
      if (!this.lastMessageAt) return null;
      const sent = new Date(this.lastMessageAt);
      if (isNaN(sent.getTime())) return null;
      const now = new Date();
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const sentStart = new Date(sent.getFullYear(), sent.getMonth(), sent.getDate());
      const diffDays = Math.round((todayStart - sentStart) / 86400000);
      if (diffDays === 0) {
        // Same calendar day — show time (e.g. "2:32 PM" / "14:32"). No forced hour12: the locale
        // decides, so fr/de get 24-hour clocks while en-US keeps its 12-hour default.
        return sent.toLocaleTimeString(getActiveLocaleTag(), { hour: 'numeric', minute: '2-digit' });
      }
      if (diffDays <= 6) {
        // 1–6 days ago — show day name (e.g. "Monday")
        return sent.toLocaleDateString(getActiveLocaleTag(), { weekday: 'long' });
      }
      // 7+ days ago — show MM/DD/YYYY
      const mm = String(sent.getMonth() + 1).padStart(2, '0');
      const dd = String(sent.getDate()).padStart(2, '0');
      const yyyy = sent.getFullYear();
      return `${mm}/${dd}/${yyyy}`;
    },
    component_key() {
      return this.messageThread.threadId?`message_thread_${this.messageThread.threadId}`:('message_thread_'+Date.now());
    },
    component_id() {
      return this.component_key+'_container';
    },
    use_image() {
      return !!this.imageSrc && !this.img_failed;
    },
    initials() {
      if (!this.name) return '';
      const parts = this.name.trim().split(/\s+/).filter(Boolean);
      if (parts.length === 0) return '';
      if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
      // Take first letter of first and last non-empty segments
      const first = parts[0].charAt(0).toUpperCase();
      const last = parts[parts.length - 1].charAt(0).toUpperCase();
      return `${first}${last}`;
    },
    text_color() {
      return this.textColor && !this.use_image ? this.textColor : this.default_text_color;
    },
    bg_color() {
      return this.bgColor && !this.use_image ? this.bgColor : this.default_bg_color;
    },
    numeric_size() {
      const s = this.size;
      const n = typeof s === 'string' ? parseInt(s, 10) : s;
      return Number.isFinite(n) && n > 0 ? n : 40;
    },
    computed_font_size() {
      if (this.fontSize) {
        const f = typeof this.fontSize === 'string' ? parseInt(this.fontSize, 10) : this.fontSize;
        return Number.isFinite(f) && f > 0 ? `${f}px` : `${Math.round(this.numeric_size * 0.45)}px`;
      }
      // Rough heuristic for initials size relative to avatar
      return `${Math.round(this.numeric_size * 0.45)}px`;
    },
    avatar_style() {
      return {
        width: this.numeric_size + 'px',
        height: this.numeric_size + 'px',
        background: this.use_image ? 'transparent' : this.bg_color,
        color: this.text_color,
        fontSize: this.computed_font_size,
        borderRadius: this.rounded ? '9999px' : '6px'
      };
    },
    img_style() {
      const z = Number.isFinite(this.imageZoom) && this.imageZoom > 0 ? this.imageZoom : 1;
      return {
        transform: `scale(${z})`
      };
    },
    alt_text() {
      if (this.alt) return this.alt;
      if (this.name) return `Avatar for ${this.name}`;
      return this.$t('messaging.avatarAlt');
    },
    avatar_img_zoom() {
      if(this.threadType === 'Threads') {
        if(this.messageThread.objectType.indexOf('SystemAI_') >= 0)
          return null;
        else
          return 0.4;
      }
      
      if(this.threadType === 'Support')
        return 1;
      
      return null;
    },
    avatar_img() {
      if(this.threadType === 'Threads') {
        if(this.messageThread.objectType.indexOf('SystemAI_') >= 0)
          return this.spark_icon;
        else if(this.messageThread.objectType === 'Appraisal' || this.messageThread.objectType === 'InventoryVehicle')
          return this.roadster_line;
        else
          return this.cube_line;
      }
    },
    // True only for the Rey spark, which is a currentColor SVG served through <img> and therefore
    // renders black. cube_line/roadster_line/tac_chat_logo carry their own colors — never tint those.
    avatar_img_is_glyph() {
      return this.avatar_img === this.spark_icon;
    },
    avatar_bg_color() {
      if(this.threadType === 'Threads') {
        if(this.messageThread.objectType === 'Appraisal' || this.messageThread.objectType === 'InventoryVehicle')
          return '#E3FFF1';
      }
      else if(this.threadType === 'Channels')
        return this.avatarColors && this.avatarColors[this.messageThread.threadId] ? this.avatarColors[this.messageThread.threadId] : this.default_bg_color;
      
      return null;
    },
    user_participants() {
      const self = this;
      if(this.$FUSarrayHasEntries(this.messageThread.participantIds)) {
        return this.messageThread.participantIds.filter(x => x.indexOf('system_')<0 && x!==self.currentUserGuid).sort((a, b) => {
          const aInUsers = !!self.usersByGuid[a];
          const bInUsers = !!self.usersByGuid[b];
          if (aInUsers && !bInUsers) return -1;
          if (!aInUsers && bInUsers) return 1;
          
          return 0;
        });
      }
      
      return [];
    },
    user_participants_count() {
      return this.user_participants.length;
    },
  },
  methods: {
    getParticipantClass(index) {
      let fieldData = 'participant_avatar_';
      if(this.user_participants_count >= 3)
        return `${fieldData}${index+1}of3`;
      if(this.user_participants_count === 2)
        return `${fieldData}${index+1}of2`;
      if(this.user_participants_count === 1)
        return `${fieldData}${index+1}of1`;
    },
    getParticipantBgColor(userParticipant, index) {
      if((index<(this.max_participants-1) || this.user_participants_count===this.max_participants) && this.avatarColors && this.avatarColors[userParticipant])
        return this.avatarColors[userParticipant];
      
      return this.default_bg_color;
    },
    getParticipantName(userParticipant, index) {
      return this.user_participants_count>this.max_participants && index>=(this.max_participants-1)?`+ ${this.user_participants_count-index}`:this.usersByGuid[userParticipant];
    },
    generateAbbreviation(firstName='', lastName='', fullName='') {
      let displayNames = fullName?fullName.split(' '):null;
      let fName = firstName || (displayNames && displayNames.length>0?displayNames[0]:'');
      let lName = lastName || (displayNames && displayNames.length>1?displayNames[1]:'');
      
      if (fullName) {
        let words = fullName.split(' ')
          .map(word => word.replace(/[()]/g, ''))
          .filter(word => word.length > 0);
        
        let abbreviation = [];
        
        if (words.length === 1)
          abbreviation.push(words[0][0]);
        else if (words.length === 2) {
          abbreviation.push(words[0][0]);
          abbreviation.push(words[1][0]);
        }
        else if (words.length >= 3) {
          abbreviation.push(words[0][0]);
          abbreviation.push(words[1][0]);
          abbreviation.push(words[2][0]);
        }
        
        return abbreviation.join('').toUpperCase();
      }
      
      let abbreviation = [];
      if(fName)
        abbreviation.push(fName[0]);
      if(lName)
        abbreviation.push(lName[0]);
      
      return abbreviation.join('').toUpperCase();
    },
  }
}
</script>

